import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getPlayersSavedStatus } from '../../actions/saved-lists-actions';

// The batch endpoint is sparse: `players` holds an entry only for ids the member has saved. Any id
// absent from the response is not saved, so callers default a missing id to isSaved=false.
export const buildSavedByIdMap = (players = []) => {
    const map = {};
    (players || []).forEach((player) => {
        if (player?.playerId != null) {
            map[player.playerId] = player.listIds || [];
        }
    });
    return map;
};

// Per-page-delta: only ids we have not already probed and are not currently fetching. Search is an
// infinite "Load More" that accumulates ids, so probing the delta keeps each request to the new page
// and never re-probes the growing accumulated list (which would blow past the 250-id server cap).
export const computePlayerIdDelta = (playerIds, probedIds, loadingIds) => (
    (playerIds || []).filter(id => id != null && !probedIds.has(id) && !loadingIds.has(id))
);

export const usePlayerSearchSavedStatus = (playerIds = [], { enabled = false } = {}) => {
    const dispatch = useDispatch();
    const [savedById, setSavedById] = useState({});
    const [loadingIds, setLoadingIds] = useState(() => new Set());
    const probedRef = useRef(new Set());
    const loadingRef = useRef(new Set());
    const isMountedRef = useRef(true);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const syncLoadingState = useCallback(() => {
        if (isMountedRef.current) {
            setLoadingIds(new Set(loadingRef.current));
        }
    }, []);

    // Shared probe: mark ids loading, fetch, hand the sparse map to applyResult.
    const runProbe = useCallback((ids, applyResult) => {
        ids.forEach(id => loadingRef.current.add(id));
        syncLoadingState();
        return dispatch(getPlayersSavedStatus(ids))
            .then((result) => {
                // Mark probed only on success. A rejected batch stays unprobed so the next page delta
                // (idsSignature change on Load More) retries it, rather than pinning those rows to
                // not-saved for the life of the mount.
                ids.forEach(id => probedRef.current.add(id));
                if (isMountedRef.current) {
                    applyResult(buildSavedByIdMap(result?.data?.players));
                }
            })
            .catch(() => {})
            .finally(() => {
                ids.forEach(id => loadingRef.current.delete(id));
                syncLoadingState();
            });
    }, [dispatch, syncLoadingState]);

    const idsSignature = playerIds.join(',');
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const delta = computePlayerIdDelta(playerIds, probedRef.current, loadingRef.current);
        if (delta.length === 0) {
            return;
        }
        runProbe(delta, map => setSavedById(prev => ({ ...prev, ...map })));
        // idsSignature captures the id-set change; playerIds identity churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, idsSignature, runProbe]);

    // Presence check via `!== undefined` (a present key always maps to an array, never undefined):
    // the CI jest runtime is Node 14, which lacks Object.hasOwn / Object.prototype.hasOwnProperty.call
    // is what SonarCloud flags — this form satisfies both.
    const getRowStatus = useCallback((playerId) => ({
        isSaved: savedById[playerId] !== undefined,
        savedListIds: savedById[playerId] || [],
        isLoading: loadingIds.has(playerId),
    }), [savedById, loadingIds]);

    // On a save/remove, re-probe just that player rather than the whole page.
    const reprobePlayer = useCallback((playerId) => runProbe([playerId], (map) => {
        setSavedById((prev) => {
            const next = { ...prev };
            if (map[playerId] !== undefined) {
                next[playerId] = map[playerId];
            } else {
                delete next[playerId];
            }
            return next;
        });
    }), [runProbe]);

    return {
        getRowStatus,
        handleSaveSuccess: reprobePlayer,
        handleRemoveSuccess: reprobePlayer,
    };
};
